//© 2025 Kaitlyn Troxell
import React, { useState, useEffect } from 'react';
import { collection, addDoc, updateDoc, deleteDoc, doc, serverTimestamp, getDocs, query, orderBy } from 'firebase/firestore';
import { db } from '../firebase';
import LoadingSpinner from './LoadingSpinner';
import PageHeader from './PageHeader';

function PunchList({ user, userRole, onNavigate, currentProject, punchItems, setPunchItems, projects, onProjectChange, contractors }) {
  const [selectedCategory, setSelectedCategory] = useState('exterior');
  const [showAddItemForm, setShowAddItemForm] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({
    task: '',
    phase: '',
    contractor: '',
    notes: '',
    scheduled_date: '',
    completed: false
  });
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(true);
  const [mobileCategoriesOpen, setMobileCategoriesOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  // Load templates from Firestore
  useEffect(() => {
    const loadTemplates = async () => {
      try {
        setLoadingTemplates(true);
        const templatesRef = collection(db, 'punch_list_templates');
        const q = query(templatesRef, orderBy('order'));
        const snapshot = await getDocs(q);
        
        const templatesData = [];
        snapshot.forEach(doc => {
          const data = doc.data();
          templatesData.push({
            category: data.category,
            name: data.name,
            icon: data.icon,
            order: data.order,
            phases: data.phases || []
          });
        });
        
        setCategories(templatesData);
        
        // Set first category as selected if available
        if (templatesData.length > 0 && !selectedCategory) {
          setSelectedCategory(templatesData[0].category);
        }
      } catch (error) {
        console.error('Error loading templates:', error);
        alert('Failed to load punch list templates');
      } finally {
        setLoadingTemplates(false);
      }
    };

    loadTemplates();
  }, []);

  // Helper function to get contractor name
  const getContractorName = (contractorId) => {
    if (!contractorId || !contractors) return '';
    const contractor = contractors.find(c => c.id === contractorId);
    return contractor ? contractor.name : '';
  };

  // Print function
  const handlePrint = () => {
    const printWindow = window.open('', '_blank');
    const currentCategory = categories.find(cat => cat.category === selectedCategory);
    
    // Filter items for selected category
    const categoryItems = punchItems.filter(item => item.category === selectedCategory);
    const completedCount = categoryItems.filter(item => item.completed).length;
    const completedItems = categoryItems.filter(item => item.completed == false);
    const totalCount = completedItems.length;
    const progressPercentage = totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0;

    // Group items by phase
    const groupedByPhase = completedItems.reduce((acc, item) => {
      const phase = item.phase || 'Other';
      if (!acc[phase]) {
        acc[phase] = [];
      }
      acc[phase].push(item);
      return acc;
    }, {});

    const htmlContent = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>${currentProject?.name || 'Project'} - ${currentCategory?.name} Punch List</title>
          <style>
            body {
              font-family: Arial, sans-serif;
              padding: 15px 20px;
              max-width: 8.5in;
              margin: 0 auto;
            }
            .phase-section {
              text-align: center;
              margin-bottom: 15px;
              border-bottom: 2px solid #333;
              padding-bottom: 10px;
              page-break-inside: avoid;
            }
            .phase-section h1 {
              margin: 0 0 5px 0;
              font-size: 20px;
              color: #333;
            }
            .phase-section h2 {
              margin: 5px 0;
              font-size: 18px;
              color: #666;
            }
            .progress {
              font-size: 12px;
              color: #666;
              margin-top: 5px;
            }
            .phase-header {
              background-color: #f0f0f0;
              padding: 6px 10px;
              margin-bottom: 6px;
              border-left: 3px solid #007bff;
              display: flex;
              justify-content: space-between;
              align-items: center;
            }
            .phase-title {
              font-size: 14px;
              font-weight: 600;
              color: #333;
              margin: 0;
            }
            .phase-progress {
              font-size: 12px;
              color: #666;
            }
            .task-list {
              list-style: none;
              padding: 0;
              margin: 0;
            }
            .task-item {
              padding: 6px 10px;
              border: 1px solid #ddd;
              margin-bottom: 4px;
              display: flex;
              align-items: flex-start;
              gap: 10px;
              page-break-inside: avoid;
            }
            .task-item.completed {
              background-color: #f8f9fa;
              opacity: 0.7;
            }
            .checkbox {
              width: 16px;
              height: 16px;
              border: 2px solid #333;
              display: inline-block;
              flex-shrink: 0;
              margin-top: 2px;
            }
            .checkbox.checked::after {
              content: '✓';
              display: block;
              text-align: center;
              font-size: 12px;
              font-weight: bold;
              line-height: 12px;
            }
            .task-content {
              flex: 1;
            }
            .task-text {
              font-size: 12px;
              color: #333;
              line-height: 1.3;
              margin-bottom: 2px;
            }
            .task-text.completed {
              text-decoration: line-through;
            }
            .task-meta {
              font-size: 10px;
              color: #666;
              font-style: italic;
              margin-top: 2px;
            }
            .task-notes {
              font-size: 10px;
              color: #555;
              margin-top: 3px;
              padding-left: 10px;
              border-left: 2px solid #ddd;
            }
            .footer {
              margin-top: 15px;
              padding-top: 10px;
              border-top: 1px solid #ddd;
              text-align: center;
              font-size: 10px;
              color: #666;
            }
            @media print {
              body {
                padding: 10px 15px;
              }
              .phase-section {
                page-break-inside: avoid;
              }
              @page {
                margin: 0.5in;
              }
            }
          </style>
        </head>
        <body>
          ${Object.keys(groupedByPhase).map(phaseName => {
            const phaseItems = groupedByPhase[phaseName];
            const phaseCompleted = phaseItems.filter(item => item.completed).length;
            const phaseTotal = phaseItems.length;
            const phaseProgress = Math.round((phaseCompleted / phaseTotal) * 100);

            return `
              <div class="phase-section">
                <h1>${currentProject?.name || 'Project'}</h1>
                <h2>${currentCategory?.icon || '📋'} ${currentCategory?.name} Punch List</h2>
                <div class="phase-header">
                  <h3 class="phase-title">${phaseName}</h3>
                </div>
                <ul class="task-list">
                  ${phaseItems.map(item => {
                    const contractorName = getContractorName(item.contractor);
                    const scheduledDate = item.scheduled_date ? new Date(item.scheduled_date).toLocaleDateString() : '';
                    return `
                      <li class="task-item ${item.completed ? 'completed' : ''}">
                        <span class="checkbox ${item.completed ? 'checked' : ''}"></span>
                        <div class="task-content">
                          <div class="task-text ${item.completed ? 'completed' : ''}">${item.task}</div>
                          ${contractorName ? `<div class="task-meta">Assigned to: ${contractorName}</div>` : ''}
                          ${scheduledDate ? `<div class="task-meta">Scheduled: ${scheduledDate}</div>` : ''}
                          ${item.notes ? `<div class="task-notes">Notes: ${item.notes}</div>` : ''}
                        </div>
                      </li>
                    `;
                  }).join('')}
                </ul>
              </div>
            `;
          }).join('')}

          <div class="footer">
            <p>Generated on ${new Date().toLocaleDateString()} at ${new Date().toLocaleTimeString()}</p>
          </div>
        </body>
      </html>
    `;

    printWindow.document.write(htmlContent);
    printWindow.document.close();
    
    // Wait for content to load, then print
    printWindow.onload = () => {
      setTimeout(() => {
        printWindow.print();
      }, 250);
    };
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!user || !user.uid) {
      alert('You must be logged in to perform this action');
      return;
    }

    if (!currentProject || !currentProject.id) {
      alert('No project selected. Please select a project first.');
      return;
    }

    if (!formData.task || formData.task.trim() === '') {
      alert('Please enter a task description');
      return;
    }
    
    setLoading(true);

    try {
      if (editingItem) {
        // Update existing item
        const itemRef = doc(db, 'punch_items', editingItem.id);
        await updateDoc(itemRef, {
          task: formData.task.trim(),
          phase: formData.phase,
          contractor: formData.contractor,
          notes: formData.notes.trim(),
          scheduled_date: formData.scheduled_date || null,
          updated_at: serverTimestamp()
        });

        setPunchItems(prev => prev.map(item =>
          item.id === editingItem.id ? { 
            ...item, 
            task: formData.task.trim(), 
            phase: formData.phase,
            contractor: formData.contractor,
            notes: formData.notes.trim(),
            scheduled_date: formData.scheduled_date || null
          } : item
        ));
      } else {
        // Add new item
        const docRef = await addDoc(collection(db, 'punch_items'), {
          task: formData.task.trim(),
          phase: formData.phase,
          contractor: formData.contractor,
          notes: formData.notes.trim(),
          scheduled_date: formData.scheduled_date || null,
          completed: false,
          category: selectedCategory,
          project_id: currentProject.id,
          created_by: user.uid,
          created_at: serverTimestamp(),
          updated_at: serverTimestamp()
        });

        setPunchItems(prev => [...prev, {
          id: docRef.id,
          task: formData.task.trim(),
          phase: formData.phase,
          contractor: formData.contractor,
          notes: formData.notes.trim(),
          scheduled_date: formData.scheduled_date || null,
          completed: false,
          category: selectedCategory
        }]);
      }

      // Reset form
      setFormData({ task: '', phase: '', contractor: '', notes: '', scheduled_date: '', completed: false });
      setShowAddItemForm(false);
      setEditingItem(null);
    } catch (error) {
      console.error('Error saving punch item:', error);
      
      if (error.code === 'permission-denied') {
        alert('Permission denied. Check your security rules.');
      } else if (error.code === 'unauthenticated') {
        alert('You are not authenticated. Please log in again.');
      } else {
        alert(`Failed to save punch item: ${error.message}`);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData({
      task: item.task,
      phase: item.phase || '',
      contractor: item.contractor || '',
      notes: item.notes || '',
      scheduled_date: item.scheduled_date || '',
      completed: item.completed
    });
    setShowAddItemForm(true);
  };

  const handleDelete = async (itemId) => {
    if (!window.confirm('Are you sure you want to delete this item?')) return;

    setLoading(true);
    try {
      await deleteDoc(doc(db, 'punch_items', itemId));
      setPunchItems(prev => prev.filter(item => item.id !== itemId));
    } catch (error) {
      console.error('Error deleting punch item:', error);
      alert('Failed to delete punch item');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleComplete = async (itemId, currentStatus) => {
    setLoading(true);
    try {
      const itemRef = doc(db, 'punch_items', itemId);
      await updateDoc(itemRef, {
        completed: !currentStatus,
        updated_at: serverTimestamp()
      });

      setPunchItems(prev => prev.map(item =>
        item.id === itemId ? { ...item, completed: !currentStatus } : item
      ));
    } catch (error) {
      console.error('Error updating status:', error);
      alert('Failed to update status');
    } finally {
      setLoading(false);
    }
  };

  const initializeCategoryTasks = async () => {
    if (!user || !user.uid) {
      alert('You must be logged in to perform this action');
      return;
    }

    if (!currentProject || !currentProject.id) {
      alert('No project selected. Please select a project first.');
      return;
    }
    
    setLoading(true);
    try {
      const category = categories.find(cat => cat.category === selectedCategory);
      if (!category) {
        alert('Category not found');
        return;
      }

      const addPromises = [];
      
      // Loop through each phase and add tasks
      category.phases.forEach(phase => {
        phase.tasks.forEach(task => {
          addPromises.push(
            addDoc(collection(db, 'punch_items'), {
              task: task,
              phase: phase.name,
              contractor: '',
              notes: '',
              completed: false,
              category: selectedCategory,
              project_id: currentProject.id,
              created_by: user.uid,
              created_at: serverTimestamp(),
              updated_at: serverTimestamp()
            })
          );
        });
      });

      const docRefs = await Promise.all(addPromises);
      
      const newItems = [];
      let refIndex = 0;
      category.phases.forEach(phase => {
        phase.tasks.forEach(task => {
          newItems.push({
            id: docRefs[refIndex].id,
            task: task,
            phase: phase.name,
            contractor: '',
            notes: '',
            completed: false,
            category: selectedCategory
          });
          refIndex++;
        });
      });

      setPunchItems(prev => [...prev, ...newItems]);
    } catch (error) {
      console.error('Error initializing tasks:', error);
      alert('Failed to initialize tasks');
    } finally {
      setLoading(false);
    }
  };

  // Filter items - if searching, search all categories; otherwise filter by selected category
  const categoryItems = punchItems.filter(item => {
    const contractorName = getContractorName(item.contractor);
    const matchesSearch = searchQuery.trim() === '' || 
      item.task.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (item.phase && item.phase.toLowerCase().includes(searchQuery.toLowerCase())) ||
      (item.notes && item.notes.toLowerCase().includes(searchQuery.toLowerCase())) ||
      (contractorName && contractorName.toLowerCase().includes(searchQuery.toLowerCase()));
    
    // If there's a search query, search across all categories
    if (searchQuery.trim() !== '') {
      return matchesSearch;
    }
    
    // Otherwise, only show items from selected category
    return item.category === selectedCategory && matchesSearch;
  });
  const completedCount = categoryItems.filter(item => item.completed).length;
  const totalCount = categoryItems.length;
  const progressPercentage = totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0;

  // Group items by phase and category (when searching all)
  const groupedByPhase = categoryItems.reduce((acc, item) => {
    const phase = item.phase || 'Other';
    if (!acc[phase]) {
      acc[phase] = [];
    }
    acc[phase].push(item);
    return acc;
  }, {});

  // Group by category when searching across all categories
  const groupedByCategory = searchQuery.trim() !== '' ? categoryItems.reduce((acc, item) => {
    const category = categories.find(cat => cat.category === item.category);
    const categoryName = category ? category.name : item.category;
    if (!acc[categoryName]) {
      acc[categoryName] = [];
    }
    acc[categoryName].push(item);
    return acc;
  }, {}) : null;

  // Get current category config
  const currentCategory = categories.find(cat => cat.category === selectedCategory);

  if (loadingTemplates) {
    return <LoadingSpinner message="Loading templates..." />;
  }

  if (categories.length === 0) {
    return (
      <div style={{ 
        minHeight: '100vh',
        backgroundColor: '#f8f9fa',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <div style={{ textAlign: 'center', padding: '2rem' }}>
          <h2>No Punch List Templates Found</h2>
          <p>Please run the initialization script to set up default templates.</p>
          <p style={{ fontSize: '14px', color: '#666', marginTop: '1rem' }}>
            Run: <code style={{ background: '#f0f0f0', padding: '4px 8px', borderRadius: '4px' }}>npm run init-templates</code>
          </p>
        </div>
      </div>
    );
  }

  return (
    <>
      {loading && <LoadingSpinner message="Processing..." />}
      
      <PageHeader
        currentPage="punchlist"
        onNavigate={onNavigate}
        currentProject={currentProject}
        projects={projects}
        onProjectChange={onProjectChange}
        userRole={userRole}
        showPrint={categoryItems.length > 0}
        onPrint={handlePrint}
      />

      <div style={{ 
        minHeight: '100vh',
        backgroundColor: '#f8f9fa',
        display: 'flex',
        flexDirection: 'column'
      }}>
        {/* Main Content - Split Layout */}
        <div className="punch-list-layout" style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
          {/* Left Sidebar - Categories */}
          <div className="punch-list-sidebar" style={{
            width: '280px',
            backgroundColor: '#fff',
            borderRight: '1px solid #ddd',
            overflowY: 'auto',
            padding: '1.5rem 1rem'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-around', alignItems: 'center'}}>
              <h3 style={{ margin: 0, fontSize: '16px', color: '#333' }}>
                 Categories
              </h3>
              {/* Mobile toggle button */}
              <button 
                className="mobile-menu-btn"
                onClick={() => setMobileCategoriesOpen(!mobileCategoriesOpen)}
                style={{ padding: '4px 20px', alignment: 'center', fontSize: '18px' }}
              >
                {mobileCategoriesOpen ? '▲' : '▼'}
              </button>
            </div>
            
            <div style={{ display: mobileCategoriesOpen ? 'block' : 'none' }} className="category-list-mobile">
              {categories.map((category) => {
                const itemCount = punchItems.filter(item => item.category === category.category).length;
                const completedInCategory = punchItems.filter(item => item.category === category.category && item.completed).length;
                
                return (
                  <button
                    key={category.category}
                    onClick={() => {
                      setSelectedCategory(category.category);
                      setMobileCategoriesOpen(false);
                    }}
                    className="category-btn-mobile"
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      marginBottom: '8px',
                      border: selectedCategory === category.category ? '2px solid #007bff' : '1px solid #ddd',
                      borderRadius: '8px',
                      backgroundColor: selectedCategory === category.category ? '#e3f2fd' : '#fff',
                      cursor: 'pointer',
                      textAlign: 'left',
                      transition: 'all 0.2s',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between'
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span style={{ fontSize: '20px' }}>{category.icon}</span>
                      <span style={{ 
                        fontSize: '14px', 
                        fontWeight: selectedCategory === category.category ? '600' : '400',
                        color: '#333'
                      }}>
                        {category.name}
                      </span>
                    </div>
                    {itemCount > 0 && (
                      <span style={{ 
                        fontSize: '12px', 
                        color: completedInCategory === itemCount ? '#28a745' : '#666',
                        fontWeight: '500'
                      }}>
                        {completedInCategory}/{itemCount}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>

            {/* Desktop category list - always visible */}
            <div style={{ display: 'block' }} className="category-list-desktop">
              {categories.map((category) => {
                const itemCount = punchItems.filter(item => item.category === category.category).length;
                const completedInCategory = punchItems.filter(item => item.category === category.category && item.completed).length;
                
                return (
                  <button
                    key={category.category}
                    onClick={() => setSelectedCategory(category.category)}
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      marginBottom: '8px',
                      border: selectedCategory === category.category ? '2px solid #007bff' : '1px solid #ddd',
                      borderRadius: '8px',
                      backgroundColor: selectedCategory === category.category ? '#e3f2fd' : '#fff',
                      cursor: 'pointer',
                      textAlign: 'left',
                      transition: 'all 0.2s',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between'
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span style={{ fontSize: '20px' }}>{category.icon}</span>
                      <span style={{ 
                        fontSize: '14px', 
                        fontWeight: selectedCategory === category.category ? '600' : '400',
                        color: '#333'
                      }}>
                        {category.name}
                      </span>
                    </div>
                    {itemCount > 0 && (
                      <span style={{ 
                        fontSize: '12px', 
                        color: completedInCategory === itemCount ? '#28a745' : '#666',
                        fontWeight: '500'
                      }}>
                        {completedInCategory}/{itemCount}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Right Content - Tasks for Selected Category */}
          <div className="punch-list-content" style={{ 
            flex: 1, 
            padding: '2rem',
            overflowY: 'auto'
          }}>
            {/* Category Header with Progress */}
            <div className="punch-list-header" style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '1.5rem',
              flexWrap: 'wrap',
              gap: '1rem'
            }}>
              <div style={{ flex: '1', minWidth: '200px' }}>
                <h2 style={{ margin: 0, marginBottom: '0.5rem', fontSize: '24px' }}>
                  {searchQuery.trim() !== '' ? '🔍 Search Results' : `${currentCategory?.icon} ${currentCategory?.name}`}
                </h2>
                {totalCount > 0 && (
                  <div style={{ fontSize: '14px', color: '#666' }}>
                    {searchQuery.trim() !== '' 
                      ? `Found ${totalCount} task${totalCount !== 1 ? 's' : ''} across all categories`
                      : `${completedCount} of ${totalCount} tasks completed (${progressPercentage}%)`
                    }
                  </div>
                )}
              </div>

              {/* Search Bar */}
              <div style={{ flex: '1', minWidth: '250px', maxWidth: '400px' }}>
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search all punchlist items"
                  className="form-input"
                  style={{
                    width: '100%',
                    padding: '10px 16px',
                    fontSize: '14px',
                    border: '1px solid #ddd',
                    borderRadius: '6px'
                  }}
                />
              </div>
              
              <div className="punch-list-actions" style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', justifyContent: 'center', width: '100%' }}>
                {categoryItems.length === 0 && searchQuery.trim() === '' && (
                  <button
                    onClick={initializeCategoryTasks}
                    className="save-btn punch-action-btn"
                    style={{ backgroundColor: '#28a745', borderColor: '#28a745', flex: '1', maxWidth: '200px' }}
                  >
                    Load Default Tasks
                  </button>
                )}
                <button
                  onClick={() => {
                    setShowAddItemForm(true);
                    setEditingItem(null);
                    setFormData({ task: '', phase: '', contractor: '', notes: '', scheduled_date: '', completed: false });
                  }}
                  className="save-btn punch-action-btn"
                  style={{ flex: '1', maxWidth: '200px' }}
                >
                  + Add Task
                </button>
              </div>
            </div>

            {/* Progress Bar */}
            {totalCount > 0 && (
              <div style={{
                width: '100%',
                height: '8px',
                backgroundColor: '#e0e0e0',
                borderRadius: '4px',
                marginBottom: '2rem',
                overflow: 'hidden'
              }}>
                <div style={{
                  width: `${progressPercentage}%`,
                  height: '100%',
                  backgroundColor: '#28a745',
                  transition: 'width 0.3s ease'
                }} />
              </div>
            )}

            {/* Add/Edit Form */}
            {showAddItemForm && (
              <div style={{
                backgroundColor: '#fff',
                padding: '1.5rem',
                borderRadius: '8px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                marginBottom: '2rem'
              }}>
                <h3 style={{ marginTop: 0, marginBottom: '1rem' }}>
                  {editingItem ? 'Edit Task' : 'Add Custom Task'}
                </h3>
                
                <form onSubmit={handleSubmit}>
                  <div className="form-group">
                    <label className="form-label">Phase</label>
                    <select
                      name="phase"
                      value={formData.phase}
                      onChange={handleChange}
                      className="form-select"
                    >
                      <option value="">Select Phase (Optional)</option>
                      {currentCategory?.phases.map((phase, index) => (
                        <option key={index} value={phase.name}>{phase.name}</option>
                      ))}
                    </select>
                  </div>

                  <div className="form-group">
                    <label className="form-label">Task Description <span className="required">*</span></label>
                    <input
                      type="text"
                      name="task"
                      value={formData.task}
                      onChange={handleChange}
                      className="form-input"
                      placeholder="Enter task description..."
                      required
                      autoFocus
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Assigned Contractor</label>
                    <select
                      name="contractor"
                      value={formData.contractor}
                      onChange={handleChange}
                      className="form-select"
                    >
                      <option value="">Not Assigned</option>
                      {contractors && contractors.map((contractor) => (
                        <option key={contractor.id} value={contractor.id}>
                          {contractor.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="form-group">
                    <label className="form-label">Scheduled Date</label>
					<input
					   type="date"
					   name="scheduled_date"
					   value={formData.scheduled_date}
					   onChange={handleChange}
					   className="form-input"
					/>
				  </div>
				  <div className="form-group">
                <label className="form-label">Notes</label>
                <textarea
                  name="notes"
                  value={formData.notes}
                  onChange={handleChange}
                  className="form-input"
                  placeholder="Add any notes or details..."
                  rows="3"
                  style={{ resize: 'vertical' }}
                />
              </div>

              <div className="modal-actions" style={{ marginTop: '1rem' }}>
                <button
                  type="button"
                  onClick={() => {
                    setShowAddItemForm(false);
                    setEditingItem(null);
                  }}
                  className="modal-cancel-btn"
                >
                  Cancel
                </button>
                <button type="submit" className="save-btn">
                  {editingItem ? 'Update Task' : 'Add Task'}
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Task List Grouped by Phase or Category */}
        {categoryItems.length === 0 ? (
          <div style={{
            backgroundColor: '#fff',
            padding: '3rem',
            borderRadius: '8px',
            textAlign: 'center',
            color: '#666'
          }}>
            {searchQuery.trim() !== '' ? (
              <>
                <p style={{ fontSize: '16px', marginBottom: '1rem' }}>
                  No tasks found matching "{searchQuery}"
                </p>
                <button
                  onClick={() => setSearchQuery('')}
                  className="save-btn"
                >
                  Clear Search
                </button>
              </>
            ) : (
              <>
                <p style={{ fontSize: '16px', marginBottom: '1rem' }}>
                  No tasks for this category yet.
                </p>
                <button
                  onClick={initializeCategoryTasks}
                  className="save-btn"
                  style={{ backgroundColor: '#28a745', borderColor: '#28a745' }}
                >
                  Load Default Tasks for {currentCategory?.name}
                </button>
              </>
            )}
          </div>
        ) : searchQuery.trim() !== '' && groupedByCategory ? (
          // When searching, group by category first
          Object.keys(groupedByCategory).map(categoryName => {
            const categoryTasks = groupedByCategory[categoryName];
            const categoryCompleted = categoryTasks.filter(item => item.completed).length;
            const categoryTotal = categoryTasks.length;
            
            // Get category icon
            const categoryData = categories.find(cat => cat.name === categoryName);
            
            return (
              <div key={categoryName} style={{ marginBottom: '3rem' }}>
                {/* Category Header */}
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '1rem',
                  paddingBottom: '0.5rem',
                  borderBottom: '3px solid #007bff',
                  backgroundColor: '#f8f9fa',
                  padding: '12px 16px',
                  borderRadius: '6px'
                }}>
                  <h2 style={{ 
                    margin: 0, 
                    fontSize: '20px', 
                    fontWeight: '600',
                    color: '#007bff'
                  }}>
                    {categoryData?.icon} {categoryName}
                  </h2>
                  <span style={{ 
                    fontSize: '14px', 
                    color: '#666',
                    fontWeight: '500'
                  }}>
                    {categoryCompleted}/{categoryTotal} completed
                  </span>
                </div>

                {/* Tasks within this category */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', paddingLeft: '1rem' }}>
                  {categoryTasks.map(item => (
                    <div
                      key={item.id}
                      className="phase-task-item"
                      style={{
                        backgroundColor: '#fff',
                        padding: '1rem 1.5rem',
                        borderRadius: '8px',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                        display: 'flex',
                        alignItems: 'flex-start',
                        justifyContent: 'space-between',
                        transition: 'all 0.2s',
                        opacity: item.completed ? 0.6 : 1
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', flex: 1 }}>
                        <input
                          type="checkbox"
                          checked={item.completed}
                          onChange={() => handleToggleComplete(item.id, item.completed)}
                          style={{
                            width: '20px',
                            height: '20px',
                            cursor: 'pointer',
                            marginTop: '2px'
                          }}
                        />
                        <div style={{ flex: 1 }}>
                          <span style={{
                            fontSize: '15px',
                            color: '#333',
                            textDecoration: item.completed ? 'line-through' : 'none',
                            display: 'block',
                            marginBottom: '4px'
                          }}>
                            {item.task}
                          </span>
                          {item.phase && (
                            <span style={{
                              fontSize: '12px',
                              color: '#666',
                              fontStyle: 'italic',
                              display: 'block'
                            }}>
                              Phase: {item.phase}
                            </span>
                          )}
                          {item.contractor && (
                            <span style={{
                              fontSize: '12px',
                              color: '#007bff',
                              display: 'block',
                              marginTop: '2px'
                            }}>
                              👷 {getContractorName(item.contractor)}
                            </span>
                          )}
                          {item.scheduled_date && (
                            <span style={{
                              fontSize: '12px',
                              color: '#6c757d',
                              display: 'block',
                              marginTop: '2px'
                            }}>
                              📅 {new Date(item.scheduled_date).toLocaleDateString()}
                            </span>
                          )}
                          {item.notes && (
                            <div style={{
                              fontSize: '13px',
                              color: '#555',
                              marginTop: '6px',
                              padding: '8px',
                              backgroundColor: '#f8f9fa',
                              borderRadius: '4px',
                              borderLeft: '3px solid #ddd'
                            }}>
                              {item.notes}
                            </div>
                          )}
                        </div>
                      </div>
                      
                      <div style={{ display: 'flex', gap: '8px', marginLeft: '12px' }}>
                        <button
                          onClick={() => handleEdit(item)}
                          className="save-btn"
                          style={{ padding: '6px 12px', fontSize: '13px' }}
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDelete(item.id)}
                          className="delete-btn"
                          style={{ padding: '6px 12px', fontSize: '13px' }}
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })
        ) : (
          // Normal view - group by phase within selected category
          Object.keys(groupedByPhase).map(phaseName => {
            const phaseItems = groupedByPhase[phaseName];
            const phaseCompleted = phaseItems.filter(item => item.completed).length;
            const phaseTotal = phaseItems.length;
            const phaseProgress = Math.round((phaseCompleted / phaseTotal) * 100);

            return (
              <div key={phaseName} style={{ marginBottom: '2.5rem' }}>
                {/* Phase Header */}
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '1rem',
                  paddingBottom: '0.5rem',
                  borderBottom: '2px solid #ddd'
                }}>
                  <h3 style={{ 
                    margin: 0, 
                    fontSize: '18px', 
                    fontWeight: '600',
                    color: '#333'
                  }}>
                    {phaseName}
                  </h3>
                  <span style={{ 
                    fontSize: '14px', 
                    color: phaseCompleted === phaseTotal ? '#28a745' : '#666',
                    fontWeight: '500'
                  }}>
                    {phaseCompleted}/{phaseTotal} ({phaseProgress}%)
                  </span>
                </div>

                {/* Phase Tasks */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                  {phaseItems.map(item => (
                    <div
                      key={item.id}
                      className="phase-task-item"
                      style={{
                        backgroundColor: '#fff',
                        padding: '1rem 1.5rem',
                        borderRadius: '8px',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                        display: 'flex',
                        alignItems: 'flex-start',
                        justifyContent: 'space-between',
                        transition: 'all 0.2s',
                        opacity: item.completed ? 0.6 : 1
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', flex: 1 }}>
                        <input
                          type="checkbox"
                          checked={item.completed}
                          onChange={() => handleToggleComplete(item.id, item.completed)}
                          style={{
                            width: '20px',
                            height: '20px',
                            cursor: 'pointer',
                            marginTop: '2px'
                          }}
                        />
                        <div style={{ flex: 1 }}>
                          <span style={{
                            fontSize: '15px',
                            color: '#333',
                            textDecoration: item.completed ? 'line-through' : 'none',
                            display: 'block'
                          }}>
                            {item.task}
                          </span>
                          {item.contractor && (
                            <span style={{
                              fontSize: '12px',
                              color: '#007bff',
                              display: 'block',
                              marginTop: '4px'
                            }}>
                              👷 {getContractorName(item.contractor)}
                            </span>
                          )}
                          {item.scheduled_date && (
                            <span style={{
                              fontSize: '12px',
                              color: '#6c757d',
                              display: 'block',
                              marginTop: '2px'
                            }}>
                              📅 {new Date(item.scheduled_date).toLocaleDateString()}
                            </span>
                          )}
                          {item.notes && (
                            <div style={{
                              fontSize: '13px',
                              color: '#555',
                              marginTop: '6px',
                              padding: '8px',
                              backgroundColor: '#f8f9fa',
                              borderRadius: '4px',
                              borderLeft: '3px solid #ddd'
                            }}>
                              {item.notes}
                            </div>
                          )}
                        </div>
                      </div>
                      
                      <div style={{ display: 'flex', gap: '8px', marginLeft: '12px' }}>
                        <button
                          onClick={() => handleEdit(item)}
                          className="save-btn"
                          style={{ padding: '6px 12px', fontSize: '13px' }}
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDelete(item.id)}
                          className="delete-btn"
                          style={{ padding: '6px 12px', fontSize: '13px' }}
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  </div>
</>
);
}
export default PunchList;